import React, { useMemo, useState, useCallback, ReactNode } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  SortingState,
  ColumnFiltersState,
  PaginationState,
  RowSelectionState,
  VisibilityState,
  ColumnDef,
  Row,
  Updater,
  Column,
} from '@tanstack/react-table';
import IconSystem from 'icon-collection/icon-systems';
import { ActionMenu, ActionMenuItem } from '../ActionMenu';

// ============================================================================
// INJECT SELF-CONTAINED STYLES
// ============================================================================

if (typeof document !== 'undefined') {
  const styleId = 'syut-grid-styles';
  // Always update textContent so CSS changes are reflected immediately during
  // hot-module-replacement without requiring a hard browser refresh.
  let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = `
      /* SyutGrid Component Styles */
      .syut-grid-component {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        font-family: inherit;
      }

      .syut-grid-wrapper {
        flex: 1;
        overflow: auto;
        min-height: 0;
        /* Match table row background so empty space below sparse rows is seamless. */
        background: var(--bg-primary, #ffffff);
        scrollbar-width: none;
      }

      .syut-grid tbody tr:last-child td {
        border-bottom: none;
      }

      .syut-grid-wrapper::-webkit-scrollbar {
        display: none;
      }

      .syut-grid {
        /* table-layout: fixed gives deterministic column sizing — explicit
           widths are honored exactly and any leftover is distributed only to
           auto-width columns. A trailing '_spacer' column (width: auto) is
           the only auto-width column, so it absorbs ALL leftover horizontal
           space. This keeps data columns + the actions column packed tightly
           on the left while the header band still extends edge-to-edge.
           Wrapper has overflow:auto, so wide content still scrolls. */
        min-width: 100%;
        width: max-content;
        table-layout: fixed;
        border-collapse: collapse;
        font-size: 0.875rem;
        background: var(--bg-primary, #ffffff);
      }

      /* Spacer column: the only auto-width column under table-layout: fixed,
         so it absorbs ALL leftover row width after fixed-width columns claim
         theirs. Padding 0 so it has no visual presence beyond row/header bg. */
      .syut-grid th.spacer-column,
      .syut-grid td.spacer-column {
        width: auto;
        padding: 0;
      }

      .syut-grid th {
        position: sticky;
        top: 0;
        z-index: 1;
        padding: 0.75rem 1rem;
        text-align: left;
        font-size: 0.875rem;
        font-weight: 600;
        color: var(--text-primary, #1e293b);
        background: var(--grid-header-bg, #dbeafe);
        border-bottom: 2px solid var(--border-color, #e2e8f0);
        white-space: nowrap;
        user-select: none;
      }

      .syut-grid th.frozen-column {
        background: var(--grid-header-bg, #dbeafe);
        box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
      }

      .syut-grid td {
        padding: 0.625rem 1rem;
        color: var(--text-primary, #1e293b);
        border-bottom: 1px solid var(--border-color, #e2e8f0);
        background: var(--bg-primary, #ffffff);
      }

      .syut-grid td.frozen-column {
        background: var(--bg-primary, #ffffff);
        box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
      }

      .syut-grid tbody tr:hover td {
        background: var(--bg-secondary, #f8fafc);
      }

      .syut-grid tbody tr:hover td.frozen-column {
        background: var(--bg-secondary, #f8fafc);
      }

      .syut-grid th.action-column,
      .syut-grid td.action-column {
        text-align: center;
        width: 80px;
      }

      .syut-grid .row-select,
      .syut-grid .row-select-all {
        width: 16px;
        height: 16px;
        cursor: pointer;
        accent-color: var(--primary, #3b82f6);
      }

      /* Pagination Footer */
      .syut-grid-component .pagination-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.2rem 1rem;
        background: var(--bg-secondary, #f8fafc);
        border-top: 1px solid var(--border-color, #e2e8f0);
        flex-shrink: 0;
      }

      .syut-grid-component .pagination-info {
        font-size: 0.8rem;
        color: var(--text-muted, #64748b);
      }

      .syut-grid-component .pagination-controls {
        display: flex;
        align-items: center;
        gap: 0.375rem;
      }

      .syut-grid-component .pagination-controls select {
        padding: 0.2rem 0.375rem;
        border-radius: 0.375rem;
        border: 1px solid var(--border-color, #e2e8f0);
        font-size: 0.8rem;
        background: var(--bg-primary, #ffffff);
        color: var(--text-primary, #1e293b);
        cursor: pointer;
      }

      .syut-grid-component .page-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 1.5rem;
        height: 1.5rem;
        padding: 0 0.25rem;
        border: 1px solid var(--border-color, #e2e8f0);
        border-radius: 0.375rem;
        background: var(--bg-primary, #ffffff);
        color: var(--text-primary, #1e293b);
        cursor: pointer;
        font-size: 0.8rem;
        transition: all 0.15s ease;
      }

      .syut-grid-component .page-btn:hover:not(:disabled) {
        background: var(--bg-secondary, #f8fafc);
        border-color: var(--primary, #3b82f6);
        color: var(--primary, #3b82f6);
      }

      .syut-grid-component .page-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `;
}

// ============================================================================
// TYPES
// ============================================================================

export interface SyutGridColumn<T> {
  id: string;
  header: string | ((column: any) => ReactNode);
  accessorKey?: keyof T;
  cell?: (info: { row: Row<T>; getValue: () => any }) => ReactNode;
  size?: number;
  enableSorting?: boolean;
  enableFiltering?: boolean;
  /** Freeze this column (sticky positioning) - 'left' or 'right' */
  frozen?: 'left' | 'right';
  /**
   * When true, this column absorbs leftover horizontal space instead of the
   * trailing spacer column. Use on the most "stretchable" column in the row
   * (e.g. a Name or Description column). `size` is treated as the minimum
   * width — the column never shrinks below it. Only one flex column makes
   * sense per grid; if multiple are set, all share leftover equally.
   */
  flex?: boolean;
}

export interface SyutGridProps<T extends { id: string }> {
  /** Data to display in the table */
  data: T[];
  /** Column definitions */
  columns: SyutGridColumn<T>[];
  /** Enable row selection */
  enableRowSelection?: boolean;
  /** Enable pagination */
  enablePagination?: boolean;
  /** Page size options */
  pageSizeOptions?: number[];
  /** Default page size */
  defaultPageSize?: number;
  /** Enable sorting */
  enableSorting?: boolean;
  /** Enable column filtering (shows filter inputs in header) */
  enableColumnFilters?: boolean;
  /** Action menu items for each row */
  rowActions?: (row: T) => ActionMenuItem[];
  /** Custom empty state message */
  emptyMessage?: string;
  /** Callback when row selection changes */
  onSelectionChange?: (selectedRows: T[]) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Additional class name */
  className?: string;
  /** Column IDs to freeze on left side (in order) */
  frozenLeftColumns?: string[];
  /** Column IDs to freeze on right side (in order) */
  frozenRightColumns?: string[];
  /** Visible column IDs - controls which columns are shown */
  visibleColumns?: string[];

  // ---- Server-side pagination ----
  /** Enable server-side (manual) pagination. When true, the grid does not slice
   *  data internally — the parent is responsible for fetching per-page data. */
  manualPagination?: boolean;
  /** Total number of records on the server (required when manualPagination=true) */
  totalCount?: number;
  /** Controlled page index (0-based). Use with manualPagination. */
  currentPage?: number;
  /** Controlled page size. Use with manualPagination. */
  pageSize?: number;
  /** Called when user changes the page index. */
  onPageChange?: (pageIndex: number) => void;
  /** Called when user changes the page size. */
  onPageSizeChange?: (pageSize: number) => void;
}

// Helper type for tracking frozen column positions
interface FrozenColumnInfo {
  frozen: 'left' | 'right' | null;
  leftOffset: number;
  rightOffset: number;
}

// ============================================================================
// COLUMN FILTER COMPONENT
// ============================================================================

interface ColumnFilterProps<T> {
  column: Column<T, unknown>;
  placeholder?: string;
}

function ColumnFilter<T>({ column, placeholder }: ColumnFilterProps<T>) {
  const columnFilterValue = column.getFilterValue() as string;

  return (
    <input
      type="text"
      value={columnFilterValue ?? ''}
      onChange={(e) => column.setFilterValue(e.target.value)}
      placeholder={placeholder || 'Filter...'}
      onClick={(e) => e.stopPropagation()}
      style={{
        width: '100%',
        padding: '0.25rem 0.5rem',
        fontSize: '0.75rem',
        border: '1px solid var(--border-color)',
        borderRadius: '0.25rem',
        marginTop: '0.375rem',
        background: 'var(--bg-primary)',
      }}
    />
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * SyutGrid - A reusable data grid component built on TanStack Table
 *
 * Supports both client-side and server-side pagination:
 *
 * @example Client-side (default):
 * ```tsx
 * <SyutGrid data={allData} columns={cols} enablePagination />
 * ```
 *
 * @example Server-side pagination:
 * ```tsx
 * <SyutGrid
 *   data={pageData}
 *   columns={cols}
 *   enablePagination
 *   manualPagination
 *   totalCount={totalFromApi}
 *   currentPage={page}
 *   pageSize={size}
 *   onPageChange={setPage}
 *   onPageSizeChange={setSize}
 * />
 * ```
 */
export function SyutGrid<T extends { id: string }>({
  data,
  columns,
  enableRowSelection = false,
  enablePagination = true,
  pageSizeOptions = [10, 25, 50, 100],
  defaultPageSize = 10,
  enableSorting = true,
  enableColumnFilters = false,
  rowActions,
  emptyMessage = 'No data found',
  onSelectionChange,
  isLoading = false,
  className = '',
  frozenLeftColumns = [],
  frozenRightColumns = [],
  visibleColumns,
  // Server-side pagination props
  manualPagination = false,
  totalCount,
  currentPage,
  pageSize: controlledPageSize,
  onPageChange,
  onPageSizeChange,
}: SyutGridProps<T>) {
  // Table state
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  // Sync controlled pagination props into internal state (for manualPagination)
  const effectivePagination: PaginationState = manualPagination
    ? { pageIndex: currentPage ?? 0, pageSize: controlledPageSize ?? defaultPageSize }
    : pagination;

  // Calculate pageCount for manual mode
  const manualPageCount = manualPagination && totalCount != null
    ? Math.ceil(totalCount / (controlledPageSize ?? defaultPageSize))
    : undefined;

  // Calculate column visibility from visibleColumns prop.
  // visibleColumns controls user-toggleable data columns ONLY. The auto-injected
  // 'select' (row checkbox) and 'actions' (row menu) columns are structural and
  // always visible when their feature is enabled — they must never be hidden by
  // the ColumnSelector toolbar control.
  const columnVisibility = useMemo<VisibilityState>(() => {
    if (!visibleColumns) {
      return {};
    }
    const visibility: VisibilityState = {};
    columns.forEach((col) => {
      visibility[col.id] = visibleColumns.includes(col.id);
    });
    return visibility;
  }, [visibleColumns, columns]);

  // Compute frozen column information based on frozenLeftColumns and frozenRightColumns
  const frozenColumnMap = useMemo(() => {
    const map: Record<string, FrozenColumnInfo> = {};

    // Helper to get column width by id
    const getColumnWidth = (colId: string): number => {
      if (colId === 'select') return 50;
      if (colId === 'actions') return 80;
      const col = columns.find(c => c.id === colId);
      return col?.size || 150;
    };

    // Calculate left frozen columns (in order provided)
    let leftOffset = 0;
    frozenLeftColumns.forEach((colId) => {
      map[colId] = { frozen: 'left', leftOffset, rightOffset: 0 };
      leftOffset += getColumnWidth(colId);
    });

    // Calculate right frozen columns (in order provided, rightmost first)
    let rightOffset = 0;
    // Reverse the array so the last item in frozenRightColumns is at the rightmost position
    [...frozenRightColumns].reverse().forEach((colId) => {
      map[colId] = { frozen: 'right', leftOffset: 0, rightOffset };
      rightOffset += getColumnWidth(colId);
    });

    return map;
  }, [columns, frozenLeftColumns, frozenRightColumns]);

  // Helper function to get sticky styles for a column
  const getStickyStyles = useCallback((columnId: string): React.CSSProperties => {
    const frozenInfo = frozenColumnMap[columnId];
    if (!frozenInfo || !frozenInfo.frozen) {
      return {};
    }

    return {
      position: 'sticky',
      [frozenInfo.frozen]: frozenInfo.frozen === 'left' ? frozenInfo.leftOffset : frozenInfo.rightOffset,
      zIndex: 1,
    };
  }, [frozenColumnMap]);

  // Build TanStack Table columns
  const tableColumns = useMemo(() => {
    const cols: ColumnDef<T, any>[] = [];

    // Add selection column if enabled
    if (enableRowSelection) {
      cols.push({
        id: 'select',
        header: ({ table }) => (
          <input
            type="checkbox"
            className="row-select-all"
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            className="row-select"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 50,
        enableColumnFilter: false,
      });
    }

    // Add data columns
    columns.forEach((col) => {
      const canFilter = enableColumnFilters && col.enableFiltering !== false;
      const columnDef: ColumnDef<T, any> = {
        id: col.id,
        accessorKey: col.accessorKey,
        header: typeof col.header === 'string'
          ? ({ column }) => {
              const headerText = col.header as string;
              return (
                <div>
                  <div
                    style={{
                      cursor: enableSorting && col.enableSorting !== false ? 'pointer' : 'default',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    onClick={() => enableSorting && col.enableSorting !== false && column.toggleSorting()}
                  >
                    <span>{headerText}</span>
                    {enableSorting && col.enableSorting !== false && (
                      <span>
                        {column.getIsSorted() === 'asc' ? (
                          <IconSystem.chevronUp size={14} />
                        ) : column.getIsSorted() === 'desc' ? (
                          <IconSystem.chevronDown size={14} />
                        ) : (
                          <IconSystem.chevronUp size={14} style={{ opacity: 0.3 }} />
                        )}
                      </span>
                    )}
                  </div>
                  {canFilter && (
                    <ColumnFilter column={column} placeholder={`Filter ${headerText}...`} />
                  )}
                </div>
              );
            }
          : col.header,
        cell: col.cell
          ? (info) => col.cell!({ row: info.row, getValue: info.getValue })
          : (info) => info.getValue(),
        size: col.size,
        enableSorting: enableSorting && col.enableSorting !== false,
        enableColumnFilter: canFilter,
      };
      cols.push(columnDef);
    });

    // Add actions column if rowActions is provided
    if (rowActions) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <ActionMenu items={rowActions(row.original)} />
        ),
        size: 80,
        enableColumnFilter: false,
      });
    }

    // Trailing spacer column. Absorbs leftover horizontal space when the sum
    // of column widths is less than the wrapper width, so data + actions
    // columns stay packed on the left without a dead gap between them.
    // Skipped when a user column has `flex: true` — that column already
    // absorbs leftover space, so adding a spacer would split it.
    const hasFlexColumn = columns.some((col) => col.flex);
    if (!hasFlexColumn) {
      cols.push({
        id: '_spacer',
        header: '',
        cell: () => null,
        enableColumnFilter: false,
        enableSorting: false,
      });
    }

    return cols;
  }, [columns, enableRowSelection, enableSorting, enableColumnFilters, rowActions]);

  // Handle selection change
  const handleRowSelectionChange = useCallback((updaterOrValue: Updater<RowSelectionState>) => {
    setRowSelection((prev) => {
      const newSelection = typeof updaterOrValue === 'function'
        ? updaterOrValue(prev)
        : updaterOrValue;

      if (onSelectionChange) {
        const selectedRows = Object.keys(newSelection)
          .filter((key) => newSelection[key])
          .map((key) => data.find((row) => row.id === key))
          .filter(Boolean) as T[];
        onSelectionChange(selectedRows);
      }

      return newSelection;
    });
  }, [data, onSelectionChange]);

  // Handle pagination change — routes to parent callbacks in manual mode
  const handlePaginationChange = useCallback((updater: Updater<PaginationState>) => {
    if (manualPagination) {
      const next = typeof updater === 'function' ? updater(effectivePagination) : updater;
      if (next.pageIndex !== effectivePagination.pageIndex && onPageChange) {
        onPageChange(next.pageIndex);
      }
      if (next.pageSize !== effectivePagination.pageSize && onPageSizeChange) {
        onPageSizeChange(next.pageSize);
      }
    } else {
      setPagination(updater);
    }
  }, [manualPagination, effectivePagination, onPageChange, onPageSizeChange]);

  // Create table instance
  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      sorting,
      columnFilters,
      rowSelection,
      pagination: effectivePagination,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: handleRowSelectionChange,
    onPaginationChange: handlePaginationChange,
    // columnVisibility is controlled via visibleColumns prop, no internal setter needed
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: manualPagination ? undefined : getFilteredRowModel(),
    getPaginationRowModel: enablePagination && !manualPagination ? getPaginationRowModel() : undefined,
    manualPagination,
    ...(manualPageCount != null && { pageCount: manualPageCount }),
    enableRowSelection,
    getRowId: (row) => row.id,
  });

  return (
    <div className={`syut-grid-component ${className}`} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Table */}
      <div className="syut-grid-wrapper" style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <table className="syut-grid">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const stickyStyles = getStickyStyles(header.id);
                  const isFrozen = !!frozenColumnMap[header.id];
                  const isSpacer = header.id === '_spacer';
                  const userCol = columns.find((c) => c.id === header.id);
                  const isFlex = userCol?.flex === true;
                  return (
                    <th
                      key={header.id}
                      style={{
                        // Under table-layout: fixed, only auto-width columns
                        // share leftover row width. Spacer and any user-defined
                        // flex column are auto; everything else gets its
                        // explicit getSize() so leftover flows only to those.
                        // Flex columns use getSize() as a min-width floor so
                        // they never shrink below their intended width.
                        width: isSpacer || isFlex ? 'auto' : header.getSize(),
                        minWidth: isFlex ? header.getSize() : undefined,
                        ...stickyStyles,
                        ...(isFrozen && { zIndex: 2 }), // Higher z-index for frozen header cells
                      }}
                      className={`${header.id === 'actions' ? 'action-column' : ''} ${isSpacer ? 'spacer-column' : ''} ${isFrozen ? 'frozen-column' : ''}`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={table.getAllColumns().length}
                  style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}
                >
                  Loading...
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={table.getAllColumns().length}
                  style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const stickyStyles = getStickyStyles(cell.column.id);
                    const isFrozen = !!frozenColumnMap[cell.column.id];
                    const isActionsColumn = cell.column.id === 'actions';
                    const isSpacerColumn = cell.column.id === '_spacer';
                    return (
                      <td
                        key={cell.id}
                        className={`${isActionsColumn ? 'action-column' : ''} ${isSpacerColumn ? 'spacer-column' : ''} ${isFrozen ? 'frozen-column' : ''}`}
                        style={{
                          ...stickyStyles,
                          ...(isActionsColumn && { overflow: 'visible' }),
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {enablePagination && (
        <div className="pagination-footer" style={{ flexShrink: 0 }}>
          <div className="pagination-info">
            Showing {table.getRowModel().rows.length} of {manualPagination ? (totalCount ?? 0) : table.getFilteredRowModel().rows.length} items
            {Object.keys(rowSelection).length > 0 && (
              <span style={{ marginLeft: '1rem', color: 'var(--primary)' }}>
                ({Object.keys(rowSelection).length} selected)
              </span>
            )}
          </div>
          <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <select
              value={effectivePagination.pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
              style={{
                padding: '0.375rem 0.5rem',
                borderRadius: '0.375rem',
                border: '1px solid var(--border-color)',
                fontSize: '0.85rem',
                marginRight: '1rem',
              }}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
            <button
              className="page-btn"
              onClick={() => table.firstPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <IconSystem.chevronLeft size={16} />
              <IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
            </button>
            <button
              className="page-btn"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <IconSystem.chevronLeft size={16} />
            </button>
            <span style={{ padding: '0 0.75rem', fontSize: '0.85rem' }}>
              Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
            </span>
            <button
              className="page-btn"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <IconSystem.chevronRight size={16} />
            </button>
            <button
              className="page-btn"
              onClick={() => table.lastPage()}
              disabled={!table.getCanNextPage()}
            >
              <IconSystem.chevronRight size={16} />
              <IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SyutGrid;
